import { useEffect, useRef, useState } from "react";

import API from "@app/services/api";
import {
  usePathwayDashboard,
  usePathwayStoreApi,
  type PathwayCard,
  type PinnedQuestion,
} from "@app/zustand/dashboard-pathway";

/**
 * Keeps the board's arrangement on the SERVER, so it belongs to the doctor and not to the browser.
 *
 * Load, then write on change. The write is debounced because a drag emits a move per frame the
 * pointer crosses a card boundary, and a request per frame would be a request per frame.
 *
 * Nothing is written before the load has answered, and a reset of the board (another account
 * signing in) loads again. Armed earlier, the first write carried this browser's board — stale, or
 * the previous account's — over the saved one whenever the load took longer than the debounce.
 *
 * Failures are deliberately silent in both directions. A layout that cannot be fetched leaves the
 * local one on screen — which is the same board the reader had a moment ago — and one that cannot be
 * saved leaves it there too. Neither is worth an error banner over a question the reader did not
 * ask; what would be worth one is losing their cards, and nothing here can.
 */

const SAVE_DEBOUNCE_MS = 800;

interface BoardLayout {
  cards?: PathwayCard[];
  pinned?: PinnedQuestion[];
}

export function useBoardSync(enabled: boolean) {
  const store = usePathwayStoreApi();
  const cards = usePathwayDashboard((s) => s.cards);
  const pinned = usePathwayDashboard((s) => s.pinned);
  const hydrate = usePathwayDashboard((s) => s.hydrate);
  const epoch = usePathwayDashboard((s) => s.epoch);
  // The epoch whose load has answered; writes wait for the current one.
  const [loadedEpoch, setLoadedEpoch] = useState<number | null>(null);
  const requestedEpoch = useRef<number | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSaved = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled || requestedEpoch.current === epoch) return;
    requestedEpoch.current = epoch;
    API.get({ path: "/dashboard/pathway/board" })
      .then((res: { ok?: boolean; data?: { layout?: BoardLayout } }) => {
        const layout = res?.ok ? (res.data?.layout ?? null) : null;
        if (layout && store.getState().epoch === epoch) hydrate(layout);
      })
      .catch(() => {
        // Nothing to do: the local board is on screen and is the one the reader was using.
      })
      .finally(() => {
        if (store.getState().epoch !== epoch) return;
        // On screen now: what the server holds, or what the reader had. Nothing to write until
        // the reader changes it.
        const { cards: shown, pinned: kept } = store.getState();
        lastSaved.current = JSON.stringify({ cards: shown, pinned: kept });
        setLoadedEpoch(epoch);
      });
  }, [enabled, epoch, hydrate, store]);

  useEffect(() => {
    if (!enabled || loadedEpoch !== epoch) return;
    const payload = JSON.stringify({ cards, pinned });
    if (payload === lastSaved.current) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      lastSaved.current = payload;
      API.put({ path: "/dashboard/pathway/board", body: { layout: { cards, pinned } } }).catch(() => {
        // Let the next change try again rather than retrying on a timer: a reader who keeps working
        // gets another attempt for free, and one who stops has nothing more to save.
        lastSaved.current = null;
      });
    }, SAVE_DEBOUNCE_MS);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [enabled, epoch, loadedEpoch, cards, pinned]);
}
